import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import store from '@/store'
import { GetLocalPlayerResponse } from '@main/integrations/lcu/api/player'
import { EndOfGameResult } from './types/EndOfGameResult'
import {
  AllLiveGameData,
  GameMode
} from '@main/integrations/lcu/liveclient/types'
import { LeagueLobby } from '@/api/match'

@Module({ dynamic: true, store: store, namespaced: true, name: 'integration' })
export default class IntegrationModule extends VuexModule {
  connectionStatus: boolean = false
  userInfo: GetLocalPlayerResponse | null = null
  // TODO: dynamically detect game based on result payload type
  userResult: EndOfGameResult | AllLiveGameData | null = null
  gameMode: GameMode | null = null
  isSoloGame: boolean = false
  isWinner: boolean | null = null
  matchGameId: string | null = null
  isJoiningCustomGame: boolean = false
  joinCustomGameFailed: boolean = false
  userResultEvidence: Blob[] = []

  @Mutation
  SET_CONNECTION_STATUS(status: boolean) {
    this.connectionStatus = status
  }

  @Mutation
  SET_USER_INFO(data: GetLocalPlayerResponse | null) {
    this.userInfo = data
  }

  @Action({ rawError: true })
  async updateConnectionState(
    connected: boolean,
    matchGameId: string | null = null
  ) {
    this.context.commit('SET_MATCH_GAME_ID', matchGameId)
    this.context.commit('SET_CONNECTION_STATUS', connected)
  }

  @Action({ rawError: true })
  async updateUserInfo(data: GetLocalPlayerResponse | null) {
    this.context.commit('SET_USER_INFO', data)
  }

  @Action({ rawError: true })
  async resetResult() {
    this.context.commit('SET_USER_RESULT', null)
    this.context.commit('SET_USER_EVIDENCE', [])
    this.context.commit('SET_MATCH_GAME_ID', '')
    this.context.commit('SET_IS_SOLO_GAME', false)
    this.context.commit('SET_IS_WINNER', null)
    this.context.commit('SET_GAMEMODE', null)
  }

  @Action({ rawError: true })
  async resetIntegration() {
    this.context.commit('SET_CONNECTION_STATUS', false)
    this.context.commit('SET_IS_SOLO_GAME', false)
    this.context.commit('SET_IS_WINNER', null)
    this.context.commit('SET_GAMEMODE', null)
    this.context.commit('SET_USER_INFO', null)
    this.context.commit('SET_USER_RESULT', null)
    this.context.commit('SET_USER_EVIDENCE', [])
    this.context.commit('SET_MATCH_GAME_ID', '')
    this.context.commit('SET_IS_JOINING_CUSTOM_GAME', false)
    this.context.commit('SET_JOIN_CUSTOM_GAME_FAILED', false)
  }

  @Mutation
  SET_USER_RESULT(payload: EndOfGameResult | null) {
    this.userResult = payload
  }

  @Mutation
  SET_IS_SOLO_GAME(isSoloGame: boolean) {
    this.isSoloGame = isSoloGame
  }

  @Mutation
  SET_IS_WINNER(isWinner: boolean) {
    this.isWinner = isWinner
  }

  @Mutation
  SET_GAMEMODE(gameMode: GameMode) {
    this.gameMode = gameMode
  }

  @Mutation
  SET_USER_EVIDENCE(evidence: Blob[] = []) {
    this.userResultEvidence = evidence
  }

  @Mutation
  SET_MATCH_GAME_ID(matchGameId: string) {
    this.matchGameId = matchGameId
  }

  @Mutation
  SET_IS_JOINING_CUSTOM_GAME(value: boolean) {
    this.isJoiningCustomGame = value
  }

  @Mutation
  SET_JOIN_CUSTOM_GAME_FAILED(value: boolean) {
    this.joinCustomGameFailed = value
  }

  @Action({ rawError: true })
  async joinCustomGame(args: {
    lobby: LeagueLobby
    team: 'one' | 'two' | 'spectator'
  }) {
    try {
      await window.ipcRenderer.invoke('lol:join-custom-game', {
        id: args.lobby.customGameId,
        password: args.lobby.password,
        asSpectator: false,
        team: args.team
      })
      this.context.commit('SET_JOIN_CUSTOM_GAME_FAILED', false)
    } catch (err) {
      this.context.commit('SET_JOIN_CUSTOM_GAME_FAILED', true)
      console.error('Join custom game failed', err)
    } finally {
      this.context.commit('SET_IS_JOINING_CUSTOM_GAME', false)
    }
  }

  @Action({ rawError: true })
  async setJoiningCustomGame(value: boolean) {
    this.context.commit('SET_IS_JOINING_CUSTOM_GAME', value)
  }

  @Action({ rawError: true })
  async setJoiningCustomGameFailed(value: boolean) {
    this.context.commit('SET_JOIN_CUSTOM_GAME_FAILED', value)
  }

  @Action({ rawError: true })
  async setResult(payload: {
    result: EndOfGameResult | AllLiveGameData
    gameMode: GameMode
    isWinner: boolean
    isSoloGame: boolean
  }) {
    console.log('[Integration Store] Received Result set.')
    this.context.commit('SET_IS_WINNER', payload.isWinner)
    this.context.commit('SET_IS_SOLO_GAME', payload.isSoloGame)
    this.context.commit('SET_GAMEMODE', payload.gameMode)
    this.context.commit('SET_USER_RESULT', payload.result)
  }

  @Action({ rawError: true })
  async setEvidenceBlobs(evidence: Blob[]) {
    console.log('[Integration Store] Received Evidence.')
    this.context.commit('SET_USER_EVIDENCE', evidence)
  }

  @Action({ rawError: true })
  async addEvidenceBlob(evidence: Blob) {
    console.log('[Integration Store] Received Evidence.')
    this.context.commit('SET_USER_EVIDENCE', [
      ...this.userResultEvidence,
      evidence
    ])
  }

  get integrationStatus() {
    return this.connectionStatus
  }

  @Action({ rawError: true })
  async matchEvidenceDataURL() {
    return new Promise<string>((resolve, reject) => {
      var reader = new FileReader()
      if (!this.userResultEvidence) {
        reject(new Error('No user evidence'))
        return
      }

      reader.onloadend = () => {
        resolve(reader.result as string)
      }
      reader.readAsDataURL(this.userResultEvidence[0])
    })
  }
}
